import React from 'react';
import { TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, DollarSign, PiggyBank, LineChart as LineChartIcon, CreditCard, Calendar } from 'lucide-react';
import { formatCurrencyText } from '../../utils/chartFormatters';
import { calculateSummaryStats, formatMonthLabel } from '../../utils/wealthTracking/chartDataTransformers';
import { useCountry } from '../../CountryContext';
import { getCurrencySymbol } from '../../countryConfig';
import InfoTooltip from './InfoTooltip';

const MonthOverMonthCard = ({ currentSnapshot, previousSnapshot, isDark }) => {
  const { selectedCountry } = useCountry();
  const currencySymbol = getCurrencySymbol(selectedCountry);

  const stats = calculateSummaryStats(currentSnapshot, previousSnapshot);

  const isPositiveChange = stats.monthOverMonthChange >= 0;
  const changeColor = isPositiveChange ? 'text-green-500' : 'text-red-500';
  const changeBgColor = isPositiveChange
    ? isDark ? 'bg-green-900/20' : 'bg-green-50'
    : isDark ? 'bg-red-900/20' : 'bg-red-50';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Left Card - Current Net Worth & Change */}
      <div
        className={`rounded-lg p-6 border ${
          isDark
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        }`}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {currentSnapshot ? `${formatMonthLabel(currentSnapshot.month)} Net Worth` : 'Net Worth'}
            </h3>
            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
              <Calendar className={`w-3 h-3 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              <span className={`text-xs font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>SNAPSHOT</span>
            </div>
            <InfoTooltip
              content="Your net worth snapshot from a specific month. This is a historical record that shows: Total Assets - Total Liabilities at that point in time."
              isDark={isDark}
              position="bottom"
            />
          </div>
          <DollarSign className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
        </div>

        <div className="mb-4">
          <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {formatCurrencyText(stats.currentNetWorth, currencySymbol)}
          </p>
        </div>

        {previousSnapshot && (
          <div className={`flex items-center gap-3 p-3 rounded-lg ${changeBgColor}`}>
            <div className={`p-2 rounded-full ${isPositiveChange ? 'bg-green-500' : 'bg-red-500'}`}>
              {isPositiveChange ? (
                <ArrowUpRight className="w-4 h-4 text-white" />
              ) : (
                <ArrowDownRight className="w-4 h-4 text-white" />
              )}
            </div>
            <div className="flex-1">
              <p className={`text-sm font-semibold ${changeColor}`}>
                {isPositiveChange ? '+' : ''}
                {formatCurrencyText(stats.monthOverMonthChange, currencySymbol)}
                {' '}
                ({stats.monthOverMonthPercent.toFixed(1)}%)
              </p>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                vs {formatMonthLabel(previousSnapshot.month)}
              </p>
            </div>
          </div>
        )}

        {!previousSnapshot && (
          <div
            className={`p-3 rounded-lg text-center ${
              isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
            }`}
          >
            <p className="text-sm">Create another snapshot to see month-over-month changes</p>
          </div>
        )}
      </div>

      {/* Right Card - Change Breakdown */}
      <div
        className={`rounded-lg p-6 border ${
          isDark
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        }`}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {currentSnapshot ? formatMonthLabel(currentSnapshot.month) : 'Current Month'} Breakdown
          </h3>
          <LineChartIcon className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
        </div>

        {previousSnapshot ? (
          <div className="space-y-3">
            {/* Contributions */}
            {stats.totalContributions > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Contributions
                  </span>
                </div>
                <span className="text-sm font-semibold text-blue-500">
                  +{formatCurrencyText(stats.totalContributions, currencySymbol)}
                </span>
              </div>
            )}

            {/* Market Growth */}
            {stats.totalMarketGrowth !== 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${stats.totalMarketGrowth >= 0 ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Market {stats.totalMarketGrowth >= 0 ? 'Growth' : 'Losses'}
                  </span>
                  <InfoTooltip
                    content="Market growth is calculated automatically using: (Closing Balance - Opening Balance - Contributions + Withdrawals). This isolates investment returns from your contributions."
                    isDark={isDark}
                    position="bottom"
                  />
                </div>
                <span className={`text-sm font-semibold ${stats.totalMarketGrowth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {stats.totalMarketGrowth >= 0 ? '+' : ''}
                  {formatCurrencyText(stats.totalMarketGrowth, currencySymbol)}
                </span>
              </div>
            )}

            {/* Debt Paydown */}
            {stats.totalLiabilityReduction > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500" />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Debt Paydown
                  </span>
                  <InfoTooltip
                    content="Debt paydown represents the principal amount paid on your liabilities this month. This is the portion of your payment that reduces your debt, excluding interest. Paying down debt increases your net worth."
                    isDark={isDark}
                    position="bottom"
                  />
                </div>
                <span className="text-sm font-semibold text-purple-500">
                  +{formatCurrencyText(stats.totalLiabilityReduction, currencySymbol)}
                </span>
              </div>
            )}

            {/* Withdrawals */}
            {stats.totalWithdrawals > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Withdrawals
                  </span>
                </div>
                <span className="text-sm font-semibold text-red-500">
                  -{formatCurrencyText(stats.totalWithdrawals, currencySymbol)}
                </span>
              </div>
            )}

            {/* Interest Paid */}
            {stats.totalInterestPaid > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500" />
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Interest Paid
                  </span>
                </div>
                <span className="text-sm font-semibold text-orange-500">
                  -{formatCurrencyText(stats.totalInterestPaid, currencySymbol)}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div
            className={`p-4 rounded-lg text-center ${
              isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
            }`}
          >
            <p className="text-sm">Breakdown will appear after creating a second snapshot</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonthOverMonthCard;
